import { useState } from "react";
import { Megaphone, ShieldAlert, Mail, MessageSquareText, Users, UsersRound, UserRound, Clock, XCircle, Pencil, Loader2, ChevronDown, ChevronUp, Ban, AlertTriangle, CheckCircle2, FlaskConical, ListChecks } from "lucide-react";
import { fmtDate } from "./time";
import { RecipientsPanel } from "./RecipientsPanel";

const KindBadge = ({ b }) => {
  const personal = b.audience?.type === "people";
  if (b.kind === "safety") return <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-bold bg-redline-50 text-redline-700"><ShieldAlert className="h-3 w-3" /> Safety</span>;
  if (personal) return <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-bold bg-sky-50 text-sky-700"><MessageSquareText className="h-3 w-3" /> Message</span>;
  return <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-bold bg-sand-100 text-ink/70"><Megaphone className="h-3 w-3" /> Announcement</span>;
};

const AudienceBadge = ({ a }) => {
  const Icon = a?.type === "people" ? UserRound : a?.type === "group" ? UsersRound : Users;
  return <span data-testid="history-audience" className="inline-flex items-center gap-1 rounded-full border border-sand-200 bg-white px-2 py-0.5 text-[11px] font-semibold text-ink/70 max-w-[220px] truncate"><Icon className="h-3 w-3 shrink-0" /> {a?.label || "Everyone"}</span>;
};

const StatusBadge = ({ b, tz }) => {
  if (b.status === "scheduled") return <span data-testid={`broadcast-scheduled-badge-${b.id}`} className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-bold bg-blue-100 text-blue-700"><Clock className="h-3 w-3" /> {fmtDate(b.scheduled_at, tz)}{b.quiet_hours_snapped ? " · moved to 8 AM" : ""}</span>;
  if (b.status === "sending") {
    const s = b.stats || {};
    const done = (s.sms_sent || 0) + (s.sms_failed || 0) + (s.email_sent || 0) + (s.email_failed || 0);
    const total = (s.sms_recipients || 0) + (s.email_recipients || 0);
    return <span data-testid={`broadcast-sending-badge-${b.id}`} className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-bold bg-blue-100 text-blue-700"><Loader2 className="h-3 w-3 animate-spin" /> Sending{total ? ` · ${Math.min(done, total)} of ${total}` : ""}</span>;
  }
  if (b.status === "failed") return <span data-testid={`broadcast-failed-badge-${b.id}`} title={b.error || ""} className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-bold bg-danger-50 text-danger-700"><AlertTriangle className="h-3 w-3" /> Failed</span>;
  if (b.status === "cancelled") return <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-bold bg-sand-100 text-ink/50"><Ban className="h-3 w-3" /> Cancelled</span>;
  return null;
};

const SmsChips = ({ s }) => {
  const tracked = s.sms_delivered !== undefined;
  const failed = (s.sms_undelivered || 0) + (s.sms_failed || 0);
  return (
    <>
      <span data-testid="chip-sms-total" className="inline-flex items-center gap-1.5 rounded-full bg-sand-50 border border-sand-200 px-2.5 py-1 text-ink/70">
        <MessageSquareText className="h-3.5 w-3.5" /> {s.sms_sent || 0}/{s.sms_recipients || 0} texts
      </span>
      {tracked && s.dry_run && <span data-testid="chip-sms-dryrun" className="inline-flex items-center gap-1 rounded-full bg-amber-50 text-amber-700 px-2.5 py-1"><FlaskConical className="h-3 w-3" /> simulated</span>}
      {tracked && s.sms_delivered > 0 && <span data-testid="chip-sms-delivered" className="inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-700 px-2.5 py-1"><CheckCircle2 className="h-3 w-3" /> {s.sms_delivered} delivered</span>}
      {failed > 0 && <span data-testid="chip-sms-failed" className="inline-flex items-center gap-1 rounded-full bg-danger-50 text-danger-700 px-2.5 py-1"><XCircle className="h-3 w-3" /> {failed} failed</span>}
      {tracked && s.sms_pending > 0 && <span data-testid="chip-sms-pending" className="inline-flex items-center gap-1 rounded-full bg-sand-100 text-ink/55 px-2.5 py-1"><Clock className="h-3 w-3" /> {s.sms_pending} awaiting receipt</span>}
    </>
  );
};

const Item = ({ b, tz, tenantId, onCancel, onEdit }) => {
  const s = b.stats || {};
  const scheduled = b.status === "scheduled";
  const body = b.sms_body || b.body || b.email_body || "";
  const [who, setWho] = useState(false);
  return (
    <div data-testid={`broadcast-history-item-${b.id}`} className={`bg-white rounded-2xl border p-4 ${scheduled ? "border-blue-200 bg-blue-50/30" : "border-sand-200"}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 flex-wrap">
            <KindBadge b={b} /><AudienceBadge a={b.audience} /><StatusBadge b={b} tz={tz} />
          </div>
          {b.title && <div className="font-heading font-bold text-ink mt-1.5 truncate">{b.title}</div>}
          <p className="text-sm text-ink/70 mt-1 whitespace-pre-wrap line-clamp-3">{body}</p>
          {b.same_copy === false && b.email_body && b.email_body !== body && (
            <p className="text-xs text-ink/50 mt-1 line-clamp-2"><Mail className="inline h-3 w-3 mr-1 -mt-0.5" />{b.email_body}</p>
          )}
        </div>
        <div className="text-[11px] text-ink/40 shrink-0 text-right">{fmtDate(b.created_at)}<div className="mt-0.5 max-w-[110px] truncate">by {b.sent_by}</div></div>
      </div>
      {scheduled ? (
        <div className="flex flex-wrap items-center justify-between gap-2 mt-3 pt-3 border-t border-blue-100 text-xs">
          <span className="text-ink/50">By {b.channels?.map((c) => (c === "sms" ? "text" : "email")).join(" + ")}{b.sms_meta?.segments ? ` · ${b.sms_meta.segments} segment${b.sms_meta.segments === 1 ? "" : "s"}` : ""}</span>
          <div className="flex gap-1.5">
            <button data-testid={`broadcast-edit-${b.id}`} onClick={() => onEdit(b)}
              className="inline-flex items-center gap-1.5 rounded-full bg-white border border-sand-200 text-ink font-semibold px-3 py-1 hover:border-ink"><Pencil className="h-3.5 w-3.5" /> Edit</button>
            <button data-testid={`broadcast-cancel-${b.id}`} onClick={() => onCancel(b.id)}
              className="inline-flex items-center gap-1.5 rounded-full bg-white border border-redline-200 text-redline-700 font-semibold px-3 py-1 hover:bg-redline-50"><XCircle className="h-3.5 w-3.5" /> Cancel</button>
          </div>
        </div>
      ) : b.status === "sent" ? (
        <>
          <div className="flex flex-wrap items-center gap-2 mt-3 pt-3 border-t border-sand-100 text-xs">
            {b.channels?.includes("sms") && <SmsChips s={s} />}
            {b.channels?.includes("email") && (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-sand-50 border border-sand-200 px-2.5 py-1 text-ink/70">
                <Mail className="h-3.5 w-3.5" /> {s.email_sent || 0}/{s.email_recipients || 0} emails
                {s.email_failed ? <span className="text-danger-600">· {s.email_failed} failed</span> : null}
                {s.email_fallbacks ? <span className="text-emerald-700">· {s.email_fallbacks} instead of a failed text</span> : null}
              </span>
            )}
            {s.skipped && Object.values(s.skipped).some(Boolean) && (
              <span className="inline-flex items-center rounded-full px-2 py-1 text-ink/45">
                {[s.skipped.sms_not_opted_in && `${s.skipped.sms_not_opted_in} not opted in`, s.skipped.sms_stopped && `${s.skipped.sms_stopped} STOP`, s.skipped.sms_no_phone && `${s.skipped.sms_no_phone} no mobile`, s.skipped.email_none && `${s.skipped.email_none} no email`].filter(Boolean).join(" · ")}
              </span>
            )}
            <button type="button" data-testid={`broadcast-who-${b.id}`} onClick={() => setWho((v) => !v)}
              className="ml-auto inline-flex items-center gap-1 rounded-full border border-sand-200 bg-white px-2.5 py-1 font-semibold text-ink/70 hover:border-ink hover:text-ink">
              <ListChecks className="h-3.5 w-3.5" /> Who got it {who ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
            </button>
          </div>
          {who && <RecipientsPanel tenantId={tenantId} messageId={b.id} />}
        </>
      ) : b.error ? <p className="text-xs text-danger-600 mt-2">{b.error}</p> : null}
    </div>
  );
};

export const MessageHistory = ({ history, tz, tenantId, onCancel, onEdit, hasMore, onLoadMore, loadingMore }) => {
  const queued = history.filter((b) => b.status === "scheduled" || b.status === "sending");
  const past = history.filter((b) => b.status !== "scheduled" && b.status !== "sending");
  if (history.length === 0) {
    return (
      <div className="bg-white rounded-2xl border border-sand-200 p-12 text-center">
        <div className="h-14 w-14 rounded-2xl bg-sand-100 flex items-center justify-center mx-auto mb-3"><MessageSquareText className="h-7 w-7 text-ink/30" strokeWidth={1.5} /></div>
        <div className="font-heading text-lg font-bold text-ink">No messages yet</div>
        <div className="text-sm text-ink/50 mt-1">Sent and scheduled messages show up here.</div>
      </div>
    );
  }
  return (
    <div className="space-y-6">
      {queued.length > 0 && (
        <section data-testid="scheduled-queue">
          <h2 className="font-heading text-lg font-bold text-ink mb-3 flex items-center gap-2"><Clock className="h-4.5 w-4.5 text-blue-600" /> Scheduled <span className="text-sm font-semibold text-ink/40">{queued.length}</span></h2>
          <div className="space-y-3">{queued.map((b) => <Item key={b.id} b={b} tz={tz} tenantId={tenantId} onCancel={onCancel} onEdit={onEdit} />)}</div>
        </section>
      )}
      <section data-testid="sent-history">
        <h2 className="font-heading text-lg font-bold text-ink mb-3">Sent</h2>
        {past.length === 0 ? <p className="text-sm text-ink/50">Nothing sent yet.</p> : (
          <div className="space-y-3">{past.map((b) => <Item key={b.id} b={b} tz={tz} tenantId={tenantId} onCancel={onCancel} onEdit={onEdit} />)}</div>
        )}
        {hasMore && (
          <div className="flex justify-center pt-4">
            <button type="button" data-testid="broadcast-history-more" onClick={onLoadMore} disabled={loadingMore}
              className="inline-flex items-center gap-1.5 rounded-full border border-sand-200 bg-white px-4 py-2 text-sm font-semibold text-ink/70 hover:border-ink hover:text-ink transition-colors disabled:opacity-50">
              {loadingMore ? <><Loader2 className="h-4 w-4 animate-spin" /> Loading…</> : <>Load more <ChevronDown className="h-4 w-4" /></>}
            </button>
          </div>
        )}
      </section>
    </div>
  );
};
